// JournalTab — Trade journal, P&L calendar
import React, { useState } from 'react';
import { useTradeJournal } from '../../../hooks/useTradeJournal';
import PnLBadge from '../shared/PnLBadge';
import { FaPlus, FaEdit, FaTrash, FaCalendarAlt, FaList } from 'react-icons/fa';

const EMOTIONS = ['confident', 'neutral', 'fearful', 'greedy', 'anxious', 'disciplined'];

export default function JournalTab() {
  const { entries, calendarData, monthlySummary, addEntry, updateEntry, deleteEntry } = useTradeJournal();
  const [view, setView] = useState('list'); // list | calendar
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [form, setForm] = useState({
    symbol: '', exchange: 'NSE', side: 'BUY', quantity: '', entryPrice: '', exitPrice: '',
    pnl: '', date: new Date().toISOString().split('T')[0],
    notes: '', tags: '', emotion: 'neutral', setup: '', mistakes: '', lessons: '',
  });

  const handleSubmit = async () => {
    const entry = {
      ...form,
      quantity: parseInt(form.quantity) || 0,
      entryPrice: parseFloat(form.entryPrice) || 0,
      exitPrice: parseFloat(form.exitPrice) || 0,
      pnl: parseFloat(form.pnl) || 0,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
    };

    if (editingEntry) {
      await updateEntry(editingEntry.id, entry);
    } else {
      await addEntry(entry);
    }

    setShowForm(false);
    setEditingEntry(null);
    setForm({
      symbol: '', exchange: 'NSE', side: 'BUY', quantity: '', entryPrice: '', exitPrice: '',
      pnl: '', date: new Date().toISOString().split('T')[0],
      notes: '', tags: '', emotion: 'neutral', setup: '', mistakes: '', lessons: '',
    });
  };

  const startEdit = (entry) => {
    setEditingEntry(entry);
    setForm({
      ...entry,
      tags: (entry.tags || []).join(', '),
      quantity: String(entry.quantity || ''),
      entryPrice: String(entry.entryPrice || ''),
      exitPrice: String(entry.exitPrice || ''),
      pnl: String(entry.pnl || ''),
    });
    setShowForm(true);
  };

  // Build calendar grid for current month
  const currentMonth = new Date().toISOString().slice(0, 7);
  const calMap = {};
  calendarData.forEach(d => { calMap[d.date] = d; });

  const [year, month] = currentMonth.split('-').map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDay = new Date(year, month - 1, 1).getDay();
  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    calendarDays.push({ day: d, date: dateStr, data: calMap[dateStr] || null });
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setView('list')}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-medium flex items-center gap-1.5 transition-all ${
              view === 'list' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FaList className="text-[9px]" /> Journal
          </button>
          <button
            onClick={() => setView('calendar')}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-medium flex items-center gap-1.5 transition-all ${
              view === 'calendar' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FaCalendarAlt className="text-[9px]" /> P&L Calendar
          </button>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditingEntry(null); }}
          className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 text-[10px] font-medium flex items-center gap-1.5 hover:bg-gray-200 transition-colors"
        >
          <FaPlus className="text-[8px]" /> Add Entry
        </button>
      </div>

      {/* Monthly Summary */}
      {monthlySummary.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {monthlySummary.slice(0, 4).map(ms => (
            <div key={ms.month} className="bg-white border border-gray-200 rounded-lg shadow-sm p-3">
              <p className="text-[10px] text-gray-400 font-medium">{ms.month}</p>
              <PnLBadge value={ms.pnl} size="md" />
              <p className="text-[10px] text-gray-400 mt-1">
                {ms.trades} trades — {ms.wins} wins ({ms.trades > 0 ? ((ms.wins / ms.trades) * 100).toFixed(0) : 0}%)
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">{editingEntry ? 'Edit' : 'New'} Journal Entry</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input value={form.symbol} onChange={e => setForm({ ...form, symbol: e.target.value })} placeholder="Symbol" className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <select value={form.side} onChange={e => setForm({ ...form, side: e.target.value })} className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="BUY">BUY</option>
              <option value="SELL">SELL</option>
            </select>
            <input type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} placeholder="Qty" className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <input type="number" value={form.entryPrice} onChange={e => setForm({ ...form, entryPrice: e.target.value })} placeholder="Entry Price" className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <input type="number" value={form.exitPrice} onChange={e => setForm({ ...form, exitPrice: e.target.value })} placeholder="Exit Price" className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <input type="number" value={form.pnl} onChange={e => setForm({ ...form, pnl: e.target.value })} placeholder="P&L" className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <select value={form.emotion} onChange={e => setForm({ ...form, emotion: e.target.value })} className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500">
              {EMOTIONS.map(e => <option key={e} value={e} className="capitalize">{e}</option>)}
            </select>
          </div>
          <input value={form.setup} onChange={e => setForm({ ...form, setup: e.target.value })} placeholder="Setup / Strategy name" className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes..." rows={2} className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
          <input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="Tags (comma separated)" className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <div className="flex gap-2">
            <button onClick={handleSubmit} className="px-4 py-1.5 rounded-full bg-blue-600 text-white text-[10px] font-medium hover:bg-blue-700 transition-colors">{editingEntry ? 'Update' : 'Save'}</button>
            <button onClick={() => { setShowForm(false); setEditingEntry(null); }} className="px-4 py-1.5 rounded-full bg-gray-100 text-gray-700 text-[10px] font-medium hover:bg-gray-200 transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="space-y-2">
          {entries.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 text-center text-xs text-gray-400">No journal entries yet</div>
          ) : (
            entries.map(entry => (
              <div key={entry.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold ${entry.side === 'BUY' ? 'text-green-600' : 'text-red-600'}`}>{entry.side}</span>
                    <span className="text-sm font-semibold text-gray-900">{entry.symbol}</span>
                    <span className="text-[10px] text-gray-400">{entry.exchange}</span>
                    <span className="text-[10px] text-gray-400">x{entry.quantity}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PnLBadge value={entry.pnl} size="sm" />
                    <span className="text-[10px] text-gray-400">{entry.date}</span>
                    <button onClick={() => startEdit(entry)} className="p-1 text-gray-400 hover:text-blue-600"><FaEdit className="text-[10px]" /></button>
                    <button onClick={() => deleteEntry(entry.id)} className="p-1 text-gray-400 hover:text-red-500"><FaTrash className="text-[10px]" /></button>
                  </div>
                </div>
                {entry.setup && <p className="text-[10px] text-blue-600 mb-1">Setup: {entry.setup}</p>}
                {entry.notes && <p className="text-[10px] text-gray-600">{entry.notes}</p>}
                {entry.tags?.length > 0 && (
                  <div className="flex gap-1 mt-1">
                    {entry.tags.map(tag => (
                      <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Calendar View */}
      {view === 'calendar' && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">{currentMonth} P&L Calendar</h3>
          <div className="grid grid-cols-7 gap-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="text-center text-[9px] font-semibold text-gray-400 py-1">{d}</div>
            ))}
            {calendarDays.map((day, i) => (
              <div
                key={i}
                className={`aspect-square rounded-lg p-1 text-center ${
                  day ? 'bg-gray-50 border border-gray-100' : ''
                } ${day?.data ? (day.data.pnl >= 0 ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50') : ''}`}
              >
                {day && (
                  <>
                    <p className="text-[10px] text-gray-400">{day.day}</p>
                    {day.data && (
                      <p className={`text-[9px] font-mono font-semibold ${day.data.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {day.data.pnl >= 0 ? '+' : ''}{day.data.pnl.toFixed(0)}
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
